import hash from 'hash.js';
import { get, map, isEqual } from 'lodash';

import Log from 'utils/Log';
import Settings from 'utils/Settings';

const DynamicYield = class {
  static generateHashedEmail(email) {
    return hash.sha256().update(email).digest('hex');
  }

  static formatPriceToFloat(price) {
    return Number((price / 100).toFixed(2));
  }

  static hasContextChanged(contextToUpdate) {
    const currentContext = window.DY.recommendationContext;
    return !isEqual(currentContext, contextToUpdate);
  }

  static updateContext(type, data) {
    // check if window script was set correctly
    if (window.DY && window.DY.API) {
      const context = {
        type,
        data,
      };

      // if context hasn't changed do nothing
      if (DynamicYield.hasContextChanged(context)) {
        try {
          window.DY.API('spa', {
            context,
            url: window.location.href,
            countAsPageview: false,
          });
          window.DY.recommendationContext = context;
        } catch (error) {
          Log.error(error, `Unable to push spa event:${type}`);
        }
      }
    } else {
      Log.warn('DY not set; can not update context');
    }
  }

  static pushEvent(eventType, data) {
    // check if window script was set correctly
    if (window.DY && window.DY.API) {
      try {
        window.DY.API('event', {
          name: eventType,
          properties: { ...data },
        });
      } catch (error) {
        Log.error(error, `Unable to push event:${eventType}`);
      }
    } else {
      Log.warn('DY not set; can not push event');
    }
  }

  static pushAddToCart(addedItem, mappedCartItems) {
    const currencyCode = Settings.currency;

    const { sku, size, price, quantity } = addedItem;

    DynamicYield.pushEvent('Add to Cart', {
      dyType: 'add-to-cart-v1',
      value: DynamicYield.formatPriceToFloat(price),
      currency: currencyCode,
      productId: sku,
      quantity,
      size,
      cart: mappedCartItems,
    });
  }

  static pushRemoveFromCart(addedItem, mappedCartItems) {
    const currencyCode = Settings.currency;

    const { sku, size, price, quantity } = addedItem;

    DynamicYield.pushEvent('Remove from Cart', {
      dyType: 'remove-from-cart-v1',
      value: DynamicYield.formatPriceToFloat(price),
      currency: currencyCode,
      productId: sku,
      quantity,
      size,
      cart: mappedCartItems,
    });
  }

  static purchaseEvent(lineItemData, orderData, currencyCode) {
    const mappedLineItems = map(lineItemData, lineItem => ({
      itemPrice: get(lineItem, 'price'),
      productId: get(lineItem, 'sku'),
      quantity: get(lineItem, 'quantity'),
      size: get(lineItem, 'size'),
    }));

    DynamicYield.pushEvent('Purchase', {
      dyType: 'purchase-v1',
      uniqueTransactionId: get(orderData, 'name'),
      value: get(orderData, 'totalPrice'),
      currency: currencyCode,
      cart: mappedLineItems,
    });
  }

  static syncCartEvent(mappedCartItems) {
    const currencyCode = Settings.currency;

    DynamicYield.pushEvent('Sync Cart', {
      dyType: 'sync-cart-v1',
      currency: currencyCode,
      cart: mappedCartItems,
    });
  }

  static emailSignUpEvent(email) {
    const hashedEmail = DynamicYield.generateHashedEmail(email);

    DynamicYield.pushEvent('Signup', {
      dyType: 'signup-v1',
      hashedEmail,
    });
  }

  static emailLoginEvent(email) {
    const hashedEmail = DynamicYield.generateHashedEmail(email);

    DynamicYield.pushEvent('Login', {
      dyType: 'login-v1',
      hashedEmail,
    });
  }

  static emailNewsletterEvent(email) {
    const hashedEmail = DynamicYield.generateHashedEmail(email);

    DynamicYield.pushEvent('Newsletter Subscription', {
      dyType: 'newsletter-subscription-v1',
      hashedEmail,
    });
  }

  static smsMarketingEvent() {
    DynamicYield.pushEvent('SMS Marketing Subscription');
  }

  static cartOpenEvent() {
    DynamicYield.pushEvent('Cart Open With Items');
  }
};

export default DynamicYield;
